import React, { useState } from "react";
import { Helmet } from 'react-helmet-async';
import useNativeSearch from './useNativeSearch';
import RenderItem from './RenderItem';
import RefinementCheckboxList from './RefinementCheckboxList';
import Pagination from './Pagination';
import '../../sass/mobilesrc.scss';

const PriceFacet = ({ items, selected, onChange }) => {
    const hasSelection = selected.min != null || selected.max != null;
    return (
        <ul className="ais-NumericMenu-list">
            <li
                className={
                    "ais-NumericMenu-item" +
                    (!hasSelection ? " ais-NumericMenu-item--selected" : "")
                }
            >
                <label className="ais-NumericMenu-label">
                    <input
                        type="radio"
                        name="price"
                        className="ais-NumericMenu-radio"
                        checked={!hasSelection}
                        onChange={() => onChange(null, null)}
                    />
                    <span className="ais-NumericMenu-labelText">All prices</span>
                </label>
            </li>
            {items?.map((b) => {
                const active =
                    (selected.min ?? null) === (b.min ?? null) &&
                    (selected.max ?? null) === (b.max ?? null);
                return (
                    <li
                        key={b.label}
                        className={
                            "ais-NumericMenu-item" +
                            (active ? " ais-NumericMenu-item--selected" : "")
                        }
                    >
                        <label className="ais-NumericMenu-label">
                            <input
                                type="radio"
                                name="price"
                                className="ais-NumericMenu-radio"
                                checked={active}
                                onChange={() => onChange(b.min, b.max)}
                            />
                            <span className="ais-NumericMenu-labelText">{b.label}</span>
                        </label>
                    </li>
                );
            })}
        </ul>
    );
};

const Panel = ({ header, children }) => (
    <div className="ais-Panel">
        <div className="ais-Panel-header">{header}</div>
        <div className="ais-Panel-body">{children}</div>
    </div>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" className="sc-8235fce6-13 eQmbcY">
        <path stroke="#1D2B36" strokeLinecap="round" strokeWidth="1.5" d="M4.5 1.5v2.25M13.5 13.5v3"></path>
        <path fill="#1D2B36" d="M5.25 11.25v-.75h-1.5v.75h1.5Zm-1.5 5.25a.75.75 0 0 0 1.5 0h-1.5Zm0-5.25v5.25h1.5v-5.25h-1.5ZM14.25 1.5a.75.75 0 0 0-1.5 0h1.5ZM12.75 6v.75h1.5V6h-1.5Zm0-4.5V6h1.5V1.5h-1.5Z"></path>
        <path fill="#1D2B36" fillRule="evenodd" d="M7.5 6a3 3 0 1 0-6 0 3 3 0 0 0 6 0Zm-3-1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm12 6.75a3 3 0 1 0-6 0 3 3 0 0 0 6 0Zm-3-1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" clipRule="evenodd"></path>
    </svg>
);

const SearchSmall = () => {
    const {
        state, data, loading,
        setQuery, toggleFilter, setPrice, setSort, setPage, setFacetQuery, clearAll,
    } = useNativeSearch();

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const handleToggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const handleCloseSidebar = () => setIsSidebarOpen(false);

    const total = data?.total ?? 0;
    const query = data?.query ?? '';

    return (
        <>
            <Helmet>
                <title>Search Dental Products Online | DentalStores.in</title>
                <meta name="description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="keywords" content="dental product search, buy dental instruments, best dental supplies, dental materials online, dental e-commerce" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://www.dentalstores.in/search" />

                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.dentalstores.in/search" />
                <meta property="og:title" content="Search Dental Products Online | DentalStores.in" />
                <meta property="og:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta property="og:image" content="https://www.dentalstores.in/logo.svg" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Search Dental Products Online | DentalStores.in" />
                <meta name="twitter:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="twitter:image" content="https://www.dentalstores.in/logo.svg" />
            </Helmet>
            <div className="search-container">
                <div className="search-input ais-SearchBox">
                    <input
                        className="ais-SearchBox-input"
                        type="search"
                        value={state.q}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search for products, brands..."
                    />
                </div>

                <div className="results-info">
                    {query ? (
                        <p className="results-info-count">Showing <b>{total}</b> results for <b>"{query}"</b></p>
                    ) : (
                        <p></p>
                    )}
                </div>

                <div className="first-row">
                    <div className="filters-btn" onClick={handleToggleSidebar}>
                        <FilterIcon />
                        Filters
                    </div>

                    <select
                        className="ais-SortBy-select"
                        value={state.sort}
                        onChange={(e) => setSort(e.target.value)}
                    >
                        <option value="relevance">Relevance</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                    </select>
                </div>

                <div className={`filters-sidebar-wrapper ${isSidebarOpen ? 'open' : ''}`}>
                    <div className="filters-sidebar-heading">
                        <FilterIcon />
                        Filters
                    </div>
                    <div className="filters-container">
                        <Panel header="Status">
                            <RefinementCheckboxList
                                items={data?.facets?.stock_status ?? []}
                                selected={state.filters.stock_status}
                                onToggle={(v) => toggleFilter('stock_status', v)}
                            />
                        </Panel>

                        <Panel header="Categories">
                            <input
                                type="text"
                                className="ais-SearchBox-input"
                                placeholder="Search categories..."
                                onChange={(e) => setFacetQuery('categories', e.target.value)}
                            />
                            <RefinementCheckboxList
                                items={data?.facets?.categories ?? []}
                                selected={state.filters.categories}
                                onToggle={(v) => toggleFilter('categories', v)}
                                limit={10}
                            />
                        </Panel>

                        <Panel header="Brands">
                            <input
                                type="text"
                                className="ais-SearchBox-input"
                                placeholder="Search brands..."
                                onChange={(e) => setFacetQuery('brand', e.target.value)}
                            />
                            <RefinementCheckboxList
                                items={data?.facets?.brand ?? []}
                                selected={state.filters.brand}
                                onToggle={(v) => toggleFilter('brand', v)}
                                limit={5}
                            />
                        </Panel>

                        <Panel header="Price Range">
                            <div className="custom-price-range">
                                <PriceFacet
                                    items={data?.facets?.price ?? []}
                                    selected={state.filters.price}
                                    onChange={setPrice}
                                />
                            </div>
                        </Panel>
                    </div>
                </div>
                {isSidebarOpen && (
                    <div className="filters-sidebar-backdrop" onClick={handleCloseSidebar}></div>
                )}

                <div className='product_wrapper' style={{ display: 'block' }}>
                    {loading && <div className="search-loading">Loading…</div>}
                    {!loading && data?.hits?.length === 0 ? (
                        <h6>Sorry, we couldn't find any results.</h6>
                    ) : (
                        <div className="products-grid">
                            {data?.hits?.map((hit) => (
                                <RenderItem key={hit.objectID} hit={hit} />
                            ))}
                        </div>
                    )}
                    <Pagination
                        page={state.page}
                        totalPages={
                            data?.total_pages ??
                            (data?.total ? Math.ceil(data.total / state.per_page) : 1)
                        }
                        onChange={setPage}
                    />
                </div>
            </div>
        </>
    );
};

export default SearchSmall;
